import { useEffect, type CSSProperties } from 'react'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import type { Loan } from '@/types'
import { dealVersion } from '@/services/dealFile'
import { loansService } from '@/services/loans'
import { invalidateLoanQueries, LOAN_QUERY_KEYS } from '@/services/loanQueryKeys'
import { ResponsiblePartySelect } from './ResponsiblePartySelect'

/** Persist by the record id and the version this row actually loaded. */
export function ResponsiblePartyCell({ loan, selectClassName, selectStyle }: {
  loan: Loan
  selectClassName?: string
  selectStyle?: CSSProperties
}) {
  const queryClient = useQueryClient()
  const version = dealVersion(loan as unknown as Record<string, unknown>)
  const save = useMutation({
    mutationFn: ({ id, assignee, version: submittedVersion }: { id: string; assignee: string; version: string | null }) =>
      loansService.update(id, { assignee }, submittedVersion),
    onSuccess: async (saved, submitted) => {
      // The server's response is authoritative. Update every cached book by id
      // before refreshing the dossier, audit and other readers of this record.
      queryClient.setQueriesData<Loan[]>({ queryKey: LOAN_QUERY_KEYS.loans }, (rows) =>
        Array.isArray(rows)
          ? rows.map((row) => row.id === submitted.id
            ? { ...row, ...saved }
            : row)
          : rows,
      )
      await invalidateLoanQueries(queryClient, submitted.id)
    },
  })
  const { isError, variables, reset } = save
  useEffect(() => {
    // A refresh may finish before the rejected request. Compare the submitted
    // version, so either event order recovers when a current row is available.
    if (isError && variables
      && (variables.id !== loan.id || variables.version !== version)) reset()
  }, [loan.id, version, isError, variables, reset])
  const status = (save.error as { response?: { status?: number } } | null)?.response?.status
  const message = status === 409
    ? 'This deal changed. Refresh before selecting again.'
    : status === 428
      ? 'Refresh this deal before selecting a Responsible Party.'
      : 'Responsible Party could not be saved. Try again.'

  return (
    <div
      className="relative w-full min-w-0"
      onClick={(event) => event.stopPropagation()}
      onDoubleClick={(event) => event.stopPropagation()}
      onKeyDown={(event) => event.stopPropagation()}
      title={save.isError ? message : undefined}
    >
      <ResponsiblePartySelect
        value={loan.assignee ?? ''}
        ariaLabel={`Responsible Party for ${loan.property || loan.id} (${loan.id})`}
        disabled={save.isPending || status === 409 || status === 428}
        onChange={(value) => {
          if (value !== loan.assignee) save.mutate({ id: loan.id, assignee: value, version })
        }}
        className={selectClassName ?? 'h-5 w-full min-w-0 rounded border border-amber-300/20 bg-[#121212] px-0.5 text-center text-inherit text-stone-300 outline-none focus-visible:border-amber-200 disabled:opacity-60'}
        style={selectStyle}
      />
      {save.isPending ? <span role="status" className="sr-only">Saving Responsible Party</span> : null}
      {save.isError ? (
        <span role="alert" className="absolute right-0 top-0 text-red-300">!<span className="sr-only">{message}</span></span>
      ) : null}
    </div>
  )
}
